import { useSuspenseQuery } from '@tanstack/react-query';
import { Trans } from '@lingui/react/macro';
import { Building2, Plus } from 'lucide-react';
import { Button } from '@/common/components/ui/button';
import { useModal } from '@/common/components/modal-provider/hooks/useModal';
import { organizationQueries } from '@/features/organizations/queries';
import { CreateOrgDialog } from '@/features/organizations/components/create-org-dialog/CreateOrgDialog';
import { cn } from '@/lib/utils';
import { OrgCard } from '@/features/organizations/components/org-card/OrgCard.tsx';

export function OrganizationsPage() {
  const showModal = useModal();
  const { data: orgs } = useSuspenseQuery(organizationQueries.list());

  const openCreateDialog = () => {
    showModal({ content: ({ resolve }) => <CreateOrgDialog resolve={resolve} />, maxWidth: 'md' });
  };

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            <Trans>Organizations</Trans>
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            <Trans>Manage organizations, their members, and supply point access.</Trans>
          </p>
        </div>
        <Button onClick={openCreateDialog} className="gap-1.5 shrink-0 self-start sm:self-auto">
          <Plus size={15} />
          <Trans>Create organization</Trans>
        </Button>
      </div>

      {orgs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card py-24 text-center">
          <div className="flex size-16 items-center justify-center rounded-2xl bg-muted mb-4">
            <Building2 size={28} className="text-muted-foreground/40" />
          </div>
          <p className="text-foreground font-medium">
            <Trans>No organizations</Trans>
          </p>
          <p className="text-sm text-muted-foreground mt-1 mb-6">
            <Trans>Create your first organization and invite your colleagues.</Trans>
          </p>
          <Button onClick={openCreateDialog} className="gap-1.5">
            <Plus size={14} />
            <Trans>Create organization</Trans>
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {orgs.map((org) => (
            <OrgCard key={org.id} org={org} />
          ))}

          <button
            onClick={openCreateDialog}
            className={cn(
              'flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border bg-card p-8 text-muted-foreground',
              'hover:border-primary/40 hover:text-primary transition-all min-h-[180px]'
            )}
          >
            <div className="flex size-10 items-center justify-center rounded-xl border-2 border-dashed border-current">
              <Plus size={18} />
            </div>
            <span className="text-sm font-medium">
              <Trans>Create organization</Trans>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
